import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { Tags } from "@/core/components/tags/Tags";
import { Idea } from "../model/types";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { useToast } from "@/core/hooks/useToast";

interface IdeaActionButtonsProps {
  idea: Idea;
  user: any;
  userBasicInfo: any;
  isIdeaInRequests: boolean;
  isIdeaInInvitations: boolean;
  ideaActions: {
    requestLoading: boolean;
    archiveLoading: boolean;
    publishLoading: boolean;
    restoreLoading: boolean;
    refetchIdea: () => void;
  };
}

export const IdeaActionButtons: React.FC<IdeaActionButtonsProps> = ({
  idea,
  user,
  userBasicInfo,
  isIdeaInRequests,
  isIdeaInInvitations,
  ideaActions,
}) => {
  const [processingRequest, setProcessingRequest] = React.useState<
    string | null
  >(null);
  const toast = useToast();

  // Find the invitation action from the idea object itself
  const invitationAction = React.useMemo(() => {
    if (!idea?.actions || !Array.isArray(idea.actions)) {
      return null;
    }
    return idea.actions.find((action) => action.action_type === "invitation");
  }, [idea]);

  useEffect(() => {
    // console.log(invitationAction, "invitation action");
  }, [invitationAction]);

  const handleRequestToJoin = () => {
    (window as any).showRequestConfirmModal?.();
  };

  const handleLeaveIdea = () => {
    (window as any).showLeaveConfirmModal?.();
  };

  const handleWithdrawRequest = () => {
    (window as any).showWithdrawConfirmModal?.();
  };

  const handleArchiveIdea = () => {
    (window as any).showArchiveConfirmModal?.();
  };

  const handlePublishIdea = () => {
    (window as any).showPublishConfirmModal?.();
  };

  const handleRestoreIdea = () => {
    (window as any).showRestoreConfirmModal?.();
  };

  const handleAcceptRequest = async (actionId: string) => {
    setProcessingRequest(actionId);
    try {
      await apiService.post(API_ENDPOINTS.IDEAS.IDEA_ACCEPT_DENY, {
        action_answer: "accept",
        action_id: actionId,
      });
      toast.showSuccess("Invitation accepted successfully!");
      ideaActions.refetchIdea(); // Refresh idea data
    } catch (error: any) {
      toast.showError(error?.message || "Failed to accept invitation");
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleDenyRequest = async (actionId: string) => {
    setProcessingRequest(actionId);
    try {
      await apiService.post(API_ENDPOINTS.IDEAS.IDEA_ACCEPT_DENY, {
        action_answer: "deny",
        action_id: actionId,
      });
      toast.showInfo("Invitation denied.");
      ideaActions.refetchIdea(); // Refresh idea data
    } catch (error: any) {
      toast.showError(error?.message || "Failed to deny invitation");
    } finally {
      setProcessingRequest(null);
    }
  };

  return (
    <>
      {!idea?.is_owner && !idea?.is_member && !idea?.is_coordinator && (
        <div className="freelancer-partnership">
          <div className="label">Partnership</div>
          <Tags
            type="partner"
            number={
              idea?.partnership
                ? Array.isArray(idea.partnership)
                  ? idea.partnership_count
                  : typeof idea.partnership === "object" &&
                      idea.partnership !== null
                    ? Object.keys(idea.partnership).length
                    : 0
                : 0
            }
          />
        </div>
      )}

      {!idea?.is_owner &&
        (isIdeaInRequests ? (
          <button
            className="withdraw-request-btn"
            onClick={handleWithdrawRequest}
            disabled={ideaActions.requestLoading}
          >
            <SvgIcon icon="icon-withdraw" />
            <div>
              {ideaActions.requestLoading
                ? "Withdrawing..."
                : "Withdraw request"}
            </div>
          </button>
        ) : invitationAction ? (
          <div className="invitation-buttons">
            <button
              className="accept-invitation-btn"
              onClick={() => handleAcceptRequest(invitationAction.action_id)}
              disabled={processingRequest === invitationAction.action_id}
            >
              <SvgIcon icon="icon-accept" />
              <div>
                {processingRequest === invitationAction.action_id
                  ? "Accepting..."
                  : "Accept invitation and join"}
              </div>
            </button>
            <button
              className="deny-invitation-btn"
              onClick={() => handleDenyRequest(invitationAction.action_id)}
              disabled={processingRequest === invitationAction.action_id}
            >
              <SvgIcon icon="icon-deny" />
              <div>
                {processingRequest === invitationAction.action_id
                  ? "Denying..."
                  : "Deny invitation"}
              </div>
            </button>
          </div>
        ) : (
          !idea.is_owner &&
          !idea.is_member && (
            <button
              className={`request-idea-btn ${
                typeof userBasicInfo?.partner === "string" &&
                userBasicInfo.partner === "freelancer"
                  ? "disabled"
                  : ""
              }`}
              onClick={() => {
                if (
                  !(
                    typeof userBasicInfo?.partner === "string" &&
                    userBasicInfo.partner === "freelancer"
                  )
                ) {
                  handleRequestToJoin();
                }
              }}
              disabled={ideaActions.requestLoading}
            >
              <SvgIcon icon="icon-requestToJoin" />
              <div>
                {ideaActions.requestLoading
                  ? "requesting..."
                  : "Request to join"}
              </div>
            </button>
          )
        ))}

      {!idea.is_owner && idea.is_member && (
        <div className="btn-leaveIdea" onClick={handleLeaveIdea}>
          <SvgIcon icon="icon-leave" /> Leave idea
        </div>
      )}

      {!idea?.is_owner &&
        typeof userBasicInfo?.partner === "string" &&
        userBasicInfo.partner === "freelancer" && (
          <div className="request-idea-pro">
            <Link to="/settings/organization">Create an organisation</Link> to
            join an idea
          </div>
        )}

      {idea?.state === "declined" && idea?.declined_reason && (
        <div className="declinedIdea">
          <div className="declinedIdea-icon">
            <SvgIcon icon="icon-declined" />
          </div>
          <div className="declinedIdea-title">Your idea got declined</div>
          <div className="declinedIdea-texts">
            <div className="declinedIdea-text-1">
              The moderator declined your idea for the following reasons:
            </div>
            <div dangerouslySetInnerHTML={{ __html: idea?.declined_reason }} />
          </div>
        </div>
      )}

      {idea.is_owner &&
        idea?.state.toLowerCase() === "approved" &&
        idea.publish_status === 1 && (
          <button
            className="withdraw-request-btn"
            onClick={handleArchiveIdea}
            disabled={ideaActions.archiveLoading}
          >
            <SvgIcon icon="icon-archive" />
            <div>
              {ideaActions.archiveLoading ? "Archiving..." : "Archive idea"}
            </div>
          </button>
        )}

      {idea?.is_owner && idea?.state.toLowerCase() === "draft" && (
        <button
          className="publish-idea-btn"
          onClick={handlePublishIdea}
          disabled={ideaActions.publishLoading}
        >
          <SvgIcon icon="icon-publish" />
          <div>
            {ideaActions.publishLoading ? "Submitting..." : "Submit for review"}
          </div>
        </button>
      )}

      {idea?.is_owner &&
        (idea?.state.toLowerCase() === "pending" ||
          idea?.state.toLowerCase() === "declined") && (
          <button className="publish-idea-btn" disabled={true}>
            <SvgIcon icon="icon-publish" />
            <div>
              {ideaActions.publishLoading
                ? "Submitting..."
                : "Submit for review"}
            </div>
          </button>
        )}

      {idea?.is_owner && idea?.state.toLowerCase() === "archived" && (
        <>
          <button
            className="publish-idea-btn"
            onClick={handleRestoreIdea}
            disabled={ideaActions.restoreLoading}
          >
            <SvgIcon icon="icon-restore" />
            <div>
              {ideaActions.restoreLoading ? "Restoring..." : "Restore idea"}
            </div>
          </button>
          <div className="edit-idea-text">
            Your idea is archived. You can restore or edit it.
          </div>
        </>
      )}

      {idea?.is_owner &&
        (idea.state.toLowerCase() === "draft" ||
          idea.state.toLowerCase() === "declined" ||
          idea.state.toLowerCase() === "approved" ||
          idea.state.toLowerCase() === "archived") && (
          <>
            <Link to={`/idea-edit/${idea.id}`} className="edit-idea-btn">
              <SvgIcon icon="icon-edit-idea" />
              <div>Edit idea</div>
            </Link>
            {idea?.state.toLowerCase() === "declined" && (
              <div className="edit-idea-text">
                Your idea is declined. Once you make any edits you will be able
                to publish it again.
              </div>
            )}
          </>
        )}

      {idea?.is_owner && idea.state.toLowerCase() === "pending" && (
        <>
          <div className="edit-idea-btn" style={{ opacity: 0.5 }}>
            <SvgIcon icon="icon-edit-idea" />
            <div>Edit idea</div>
          </div>
          <div className="ideaPendingText">
            Your idea is under review by a moderator. We will let you know if it
            gets published or rejected.
          </div>
        </>
      )}
    </>
  );
};
